import React from "react";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  formatNumber,
} from "../utils/siteActivityFormatters";

const COLORS = [
  "#2EA7E0",
  "#123B57",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#F43F5E",
  "#64748B",
];

export default function SiteActivityCharts({
  topSitesData,
  productChartData,
  familyLabel,
  loading = false,
}) {
  return (
    <div className="grid grid-cols-1 gap-6 2xl:grid-cols-3">
      <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 2xl:col-span-2">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-[#123B57] dark:text-white">
            Top 10 —{" "}
            {familyLabel}
          </h3>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Sites les plus
            visités pour la date
            sélectionnée.
          </p>
        </div>

        {loading &&
        !topSitesData.length ? (
          <div className="flex h-[500px] items-center justify-center">
            <div className="text-sm font-semibold text-slate-400">
              Chargement des
              statistiques de la
              famille…
            </div>
          </div>
        ) : !topSitesData.length ? (
          <div className="flex h-[500px] items-center justify-center">
            <div className="max-w-sm text-center text-sm text-slate-400">
              Aucune donnée de
              visite disponible
              pour cette famille.
            </div>
          </div>
        ) : (
          <div className="h-[500px]">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={
                  topSitesData
                }
                layout="vertical"
                margin={{
                  top: 8,
                  right: 24,
                  bottom: 8,
                  left: 80,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={
                    false
                  }
                />

                <XAxis
                  type="number"
                  tick={{
                    fontSize: 12,
                  }}
                />

                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{
                    fontSize: 11,
                  }}
                  width={25}
                />

                <Tooltip
                  formatter={(
                    value,
                  ) => [
                    `${formatNumber(
                      value,
                    )} visites`,
                    "Visites",
                  ]}
                />

                <Bar
                  dataKey="value"
                  radius={[
                    0,
                    10,
                    10,
                    0,
                  ]}
                  barSize={36}
                >
                  {topSitesData.map(
                    (
                      entry,
                      index,
                    ) => (
                      <Cell
                        key={
                          entry.name
                        }
                        fill={
                          COLORS[
                            index %
                              COLORS.length
                          ]
                        }
                      />
                    ),
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-[#123B57] dark:text-white">
            Répartition des
            produits
          </h3>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Nombre de sites par
            produit dans{" "}
            {familyLabel}.
          </p>
        </div>

        <div className="h-[260px]">
          {productChartData.length ? (
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <PieChart>
                <Pie
                  data={
                    productChartData
                  }
                  dataKey="sites"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                >
                  {productChartData.map(
                    (
                      entry,
                      index,
                    ) => (
                      <Cell
                        key={
                          entry.key
                        }
                        fill={
                          COLORS[
                            index %
                              COLORS.length
                          ]
                        }
                      />
                    ),
                  )}
                </Pie>

                <Tooltip
                  formatter={(
                    value,
                  ) => [
                    `${formatNumber(
                      value,
                    )} site${
                      Number(
                        value,
                      ) > 1
                        ? "s"
                        : ""
                    }`,
                    "Sites",
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Aucune donnée
            </div>
          )}
        </div>

        <div className="mt-4 space-y-2">
          {productChartData.map(
            (
              item,
              index,
            ) => (
              <div
                key={
                  item.key
                }
                className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/60"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{
                      backgroundColor:
                        COLORS[
                          index %
                            COLORS.length
                        ],
                    }}
                  />

                  <span className="truncate font-semibold text-slate-700 dark:text-slate-200">
                    {
                      item.name
                    }
                  </span>
                </div>

                <span className="shrink-0 font-bold text-[#123B57] dark:text-white">
                  {formatNumber(
                    item.sites,
                  )}
                </span>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
}